import { Component, ViewChild, Input, OnChanges, SimpleChanges, AfterViewChecked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IntervalObservable } from 'rxjs/observable/IntervalObservable';
import 'rxjs/add/operator/takeWhile';

import { BufferChangeReason } from 'app/buffer-graph/models/buffer-change-reasons/buffer-change-reason';
import { TabComponentBase } from 'app/buffer-graph/models/tab-component-base';
import { SlickGridComponent } from 'app/slick-grid/slick-grid.component';
import { BufferGraphService } from 'app/buffer-graph/services/buffer-graph.service';
import { ISlickGridColumn } from 'app/slick-grid/interfaces/slick-grid-column';
import { EventArgs } from 'app/slick-grid/models/event-args';

@Component({
    selector: 'buffer-change-reasons',
    templateUrl: './buffer-change-reasons.component.html',
    styleUrls: ['./buffer-change-reasons.component.css']
})
export class BufferChangeReasonsComponent extends TabComponentBase<any, any> implements OnChanges, AfterViewChecked {
    constructor(graphService: BufferGraphService, private readonly translateService: TranslateService) {
        super(graphService);

        this.dataView = new Slick.Data.DataView<any>();
        this.options = { syncColumnCellResize: true, multiColumnSort: false, editable: false, enableColumnReorder: false };
        this.headersDataView = new Slick.Data.DataView<any>();
        this.headersOptions = { syncColumnCellResize: true, multiColumnSort: false, editable: false, enableColumnReorder: false, enableCellNavigation: false };

        this.translates = {};
        this.translateService.get([
            'Date',
            'Reason',
            'Supplier',
            'LastOrderDate',
            'PlannedDeliveryDate',
            'DeliveryDate',
            'DocumentInputDate',
            'InitialOrder',
            'SentOrder',
            'ArrivedOrder',
            'OrderSchedule'
        ]).subscribe(t => {
            this.translates['Date'] = t.Date;
            this.translates['Reason'] = t.Reason;
            this.translates['Supplier'] = t.Supplier;
            this.translates['LastOrderDate'] = t.LastOrderDate;
            this.translates['PlannedDeliveryDate'] = t.PlannedDeliveryDate;
            this.translates['DeliveryDate'] = t.DeliveryDate;
            this.translates['DocumentInputDate'] = t.DocumentInputDate;
            this.translates['InitialOrder'] = t.InitialOrder;
            this.translates['SentOrder'] = t.SentOrder;
            this.translates['ArrivedOrder'] = t.ArrivedOrder;
            this.translates['OrderSchedule'] = t.OrderSchedule;

            this.translatesLoaded = true;
        });
    }

    @ViewChild('grid')
    protected gridRef: SlickGridComponent;

    @Input()
    protected isActive: boolean;

    @ViewChild('headers')
    protected headersRef: SlickGridComponent;

    @Input()
    private reasons: BufferChangeReason[];

    @Input()
    private showDate: boolean;

    private readonly translates: { [key: string]: string };

    private translatesLoaded: boolean;

    protected readonly headersDataView: Slick.Data.DataView<any>;

    protected headersColumns: ISlickGridColumn<any>[];

    protected readonly headersOptions: Slick.GridOptions<any>;

    protected sku: string;

    protected warehouseCode: string;

    protected canSend: boolean;

    private scrollHandled: boolean;

    private mainViewport: HTMLElement;

    private headersViewport: HTMLElement;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['reasons'] || changes['showDate'])
            this.setData();
    }

    ngAfterViewChecked(): void {
        if (this.scrollHandled || !this.gridRef || !this.headersRef)
            return;

        this.mainViewport = this.gridRef.htmlElement.querySelector('.slick-viewport') as HTMLElement;
        this.headersViewport = this.headersRef.htmlElement.querySelector('.slick-viewport') as HTMLElement;
        this.mainViewport.addEventListener('scroll',
            () => {
                if (this.headersViewport.scrollTop !== this.mainViewport.scrollTop)
                    this.headersViewport.scrollTop = this.mainViewport.scrollTop;
            });
        this.scrollHandled = true; 
    }

    protected onTabsResized(): boolean {
        if (!super.onTabsResized())
            return false;

        setTimeout(() => {
                if (this.headersViewport && this.mainViewport) {
                    const diff = this.mainViewport.offsetHeight - this.mainViewport.clientHeight;
                    const canvas = this.headersViewport.querySelector('.grid-canvas') as HTMLElement;
                    if (canvas)
                        canvas.style.marginBottom = `${diff}px`;
                }

                if (this.headersRef)
                    this.headersRef.resizeGrid();
            },
            1);
        return true;
    }

    protected getExportData = this.getExportDataByDefault;

    private setData = (): void => {
        let done = false;
        IntervalObservable.create(10).takeWhile(() => !this.destroyed && !done).subscribe(() => {
            if (!this.translatesLoaded)
                return;

            done = true;

            const headersRows = [];
            if (this.showDate)
                headersRows.push({ id: '1', h: this.translates['Reason'] });
            headersRows.push({ id: '2', h: this.translates['Supplier'] });
            headersRows.push({ id: '3', h: this.translates['LastOrderDate'] });
            headersRows.push({ id: '4', h: this.translates['PlannedDeliveryDate'] });
            headersRows.push({ id: '5', h: this.translates['DeliveryDate'] });
            headersRows.push({ id: '6', h: this.translates['DocumentInputDate'] });
            headersRows.push({ id: '7', h: this.translates['InitialOrder'] });
            headersRows.push({ id: '8', h: this.translates['SentOrder'] });
            headersRows.push({ id: '9', h: this.translates['ArrivedOrder'] });
            headersRows.push({ id: '10', h: this.translates['OrderSchedule'] });

            const headersColumns = [{ id: 'h', field: 'h', name: this.showDate ? 'Date' : 'Reason', sortable: false, focusable: false, selectable: false, cssClass: 'ui-state-default slick-header-column' }];

            const fields = [
                'reason',
                'supplier',
                'lastOrderDate',
                'planDeliveryDate',
                'deliveryDate',
                'docInputDate',
                'initialOrder',
                'sentOrder',
                'arrivedOrder',
                'orderSchedule'
            ];

            const rows = [];
            for (const field of fields) {
                if (field === 'reason' && !this.showDate)
                    continue;

                const row = { id: `${rows.length}` };
                for (const reason of this.reasons)
                    row[reason.date.format('L')] = reason[field];
                rows.push(row);
            }
            const columns = [];
            for (let i = 0; i < this.reasons.length; i++)
                columns.push({
                    id: `col${i}`,
                    field: this.reasons[i].date.format('L'),
                    name: this.showDate ? this.reasons[i].date.format('L') : this.reasons[i].reason,
                    sortable: false
                });

            this.headersColumns = headersColumns;
            this.headersDataView.setItems(headersRows);

            this.columns = columns;
            this.dataView.setItems(rows);
        });
    }

    private onHeaderColumnsResized = (/*args: EventArgs<Slick.OnColumnsResizedEventArgs<any>>*/): void => {
        this.onTabsResized();
    }
}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/components/buffer-change-reasons.component/buffer-change-reasons.component.ts